import { Flex, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import { Button, Pill, Text, UniversalSelect } from '@feathr/components';

import GeoFilterModal from '../GeoFilterModal';
import GeoFiltersList from '../GeoFiltersList';
import type { IUseGeoFilterSetProps } from './GeoFilterSet.hooks';
import { useGeoFilterSet } from './GeoFilterSet.hooks';
import type { IGeoFilterSetOption } from './GeoFilterSet.utils';
import { getOptionLabel } from './GeoFilterSet.utils';

import * as styles from './GeoFilterSet.css';

function renderOptionOrSingleValue(
  props: SingleValueProps<IGeoFilterSetOption> | OptionProps<IGeoFilterSetOption>,
  t: TFunction,
): JSX.Element {
  const { data } = props;

  return (
    <Flex align={'center'} justify={'space-between'}>
      <span>{data.name}</span>
      {data.is_default && <Pill theme={'neutral'}>{t('Default')}</Pill>}
    </Flex>
  );
}

function CustomSingleValue(
  props: SingleValueProps<IGeoFilterSetOption>,
  t: TFunction,
): JSX.Element {
  return (
    <components.SingleValue {...props}>
      {renderOptionOrSingleValue(props, t)}
    </components.SingleValue>
  );
}

function CustomOption(props: OptionProps<IGeoFilterSetOption>, t: TFunction): JSX.Element {
  return <components.Option {...props}>{renderOptionOrSingleValue(props, t)}</components.Option>;
}

function GeoFilterSet({
  campaign,
  targetings,
  defaultGeoSetId,
  hasUserDeclinedGeofilters,
  onEditGeoFilterSet,
}: Readonly<IUseGeoFilterSetProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const {
    selectedGeoFilterSet,
    sortedGeoFilterSets,
    isLoading,
    editCreate,
    handleClearSelection,
    handleSetSelection,
    handleEditGeoFilterSetSubmit,
  } = useGeoFilterSet({
    campaign,
    targetings,
    defaultGeoSetId,
    hasUserDeclinedGeofilters,
    onEditGeoFilterSet,
  });

  return (
    <Flex direction={'column'} gap={theme.spacing[4]}>
      <Flex direction={'column'} gap={theme.spacing[2]}>
        <Text.DefaultMedium>{t('Geofilter set')}</Text.DefaultMedium>
        <Flex align={'center'} className={styles.dropdownWrapper} gap={theme.spacing[2]}>
          <UniversalSelect<IGeoFilterSetOption>
            components={{
              Option: (props) => CustomOption(props, t),
              SingleValue: (props) => CustomSingleValue(props, t),
            }}
            getOptionLabel={getOptionLabel}
            isClearable={true}
            isFullWidth={true}
            isLoading={isLoading}
            menuPortalTarget={document.body}
            name={'geofilter-set-selection'}
            noOptionsMessage={(): string => t('No saved geofilter sets found')}
            onClear={handleClearSelection}
            onSelectSingle={handleSetSelection}
            options={sortedGeoFilterSets}
            placeholder={t('Search or select a geofilter set')}
            value={selectedGeoFilterSet}
          />
          {selectedGeoFilterSet && (
            <Button onClick={editCreate.handleOpen('edit')} type={'secondary'}>
              {t('Edit geofilter set')}
            </Button>
          )}
        </Flex>
      </Flex>
      {selectedGeoFilterSet && (
        <GeoFiltersList
          excludedGeoFilterIds={selectedGeoFilterSet.excluded_geo_filters ?? []}
          includedGeoFilterIds={selectedGeoFilterSet.included_geo_filters ?? []}
        />
      )}

      <GeoFilterModal
        campaign={campaign}
        context={'edit'}
        initialData={
          selectedGeoFilterSet
            ? {
                name: selectedGeoFilterSet.name,
                is_default: selectedGeoFilterSet.is_default,
                included_geo_filters: selectedGeoFilterSet.included_geo_filters ?? [],
                excluded_geo_filters: selectedGeoFilterSet.excluded_geo_filters ?? [],
              }
            : undefined
        }
        onClose={editCreate.close}
        onSaveChanges={handleEditGeoFilterSetSubmit}
        opened={editCreate.isOpen}
      />
    </Flex>
  );
}

export default observer(GeoFilterSet);
